import Big from 'big.js';

/**
 * 将输入值转换为Big实例
 * @param value 需要转换的值
 * @returns Big实例
 */
// 在toBig函数中添加校验（可选扩展）
const toBig = (value: number | string | Big): Big => {
  if (value instanceof Big) return value;
  if (typeof value === 'string' && !/^-?\d+(\.\d+)?$/.test(value)) {
    throw new Error(`无效数值格式: ${value}`);
  }
  return new Big(value as number | string);
};

/**
 * 执行加法运算，支持无限个参数
 * @param args 要相加的数值
 * @returns 计算结果
 */
export const add = (...args: Array<number | string | Big>): Big => {
  if (args.length === 0) return new Big(0);

  return args.reduce<Big>((result, current) => {
    // 显式声明泛型类型
    return result.plus(toBig(current));
  }, new Big(0));
};

/**
 * 执行减法运算，支持无限个参数
 * 如果只有一个参数，返回其负值
 * 如果有多个参数，从第一个参数中减去其余所有参数
 * @param args 要相减的数值
 * @returns 计算结果
 */
export const subtract = (...args: Array<number | string | Big>): Big => {
  if (args.length === 0) return new Big(0);
  if (args.length === 1) return toBig(args[0]!).times(-1);

  const first = toBig(args[0]!);
  return args.slice(1).reduce<Big>((result, current) => {
    return result.minus(toBig(current));
  }, first);
};

/**
 * 执行乘法运算，支持无限个参数
 * @param args 要相乘的数值
 * @returns 计算结果
 */
export const minus = (...args: Array<number | string | Big>): Big => {
  if (args.length === 0) return new Big(1);

  return args.reduce<Big>((result, current) => {
    return result.times(toBig(current));
  }, new Big(1));
};

/**
 * 执行除法运算，支持无限个参数
 * 如果只有一个参数，返回其倒数
 * 如果有多个参数，用第一个参数除以其余所有参数
 * @param args 要相除的数值
 * @returns 计算结果
 * @throws 如果除数为0，将抛出错误
 */
export const divide = (...args: Array<number | string | Big>): Big => {
  if (args.length === 0) return new Big(1);
  if (args.length === 1) return new Big(1).div(toBig(args[0]!));

  const first = toBig(args[0]!);
  return args.slice(1).reduce<Big>((result, current) => {
    const divisor = toBig(current);
    if (divisor.eq(0)) throw new Error('除数不能为0');
    return result.div(divisor);
  }, first);
};

/**
 * 计算一个数的n次方
 * @param base 底数
 * @param exponent 指数
 * @returns 计算结果
 */
export const power = (base: number | string | Big, exponent: number): Big => {
  return toBig(base).pow(exponent);
};

/**
 * 计算一个数的平方根
 * @param value 要计算平方根的数值
 * @returns 计算结果
 * @throws 如果输入为负数，将抛出错误
 */
export const sqrt = (value: number | string | Big): Big => {
  const bigValue = toBig(value);
  if (bigValue.lt(0)) throw new Error('不能计算负数的平方根');
  return bigValue.sqrt();
};

/**
 * 将数值四舍五入到指定小数位
 * @param value 要四舍五入的数值
 * @param decimalPlaces 小数位数
 * @returns 四舍五入后的结果
 */
export const round = (value: number | string | Big, decimalPlaces = 0): Big => {
  return toBig(value).round(decimalPlaces);
};

/**
 * 比较两个数值的大小
 * @param a 第一个数值
 * @param b 第二个数值
 * @returns 如果a>b返回1，如果a<b返回-1，如果a=b返回0
 */
export const compare = (a: number | string | Big, b: number | string | Big): number => {
  return toBig(a).cmp(toBig(b));
};

/**
 * 获取绝对值
 * @param value 要计算绝对值的数值
 * @returns 绝对值
 */
export const abs = (value: number | string | Big): Big => {
  return toBig(value).abs();
};

/**
 * 将Big实例转换为数字
 * 注意：如果数值超出JavaScript的Number范围，可能会丢失精度
 * @param value Big实例
 * @returns 数字
 */
export const toNumber = (value: Big): number => {
  return value.toNumber();
};

/**
 * 将Big实例转换为字符串
 * @param value Big实例
 * @param decimalPlaces 小数位数
 * @returns 字符串表示
 */
export const toString = (value: Big, decimalPlaces?: number): string => {
  return decimalPlaces !== undefined ? value.toFixed(decimalPlaces) : value.toString();
};

/**
 * 判断是否为数字类型
 * @param value 要检查的值
 * @returns 如果是数字类型返回true，否则返回false
 */
export const isNumber = (value: any): boolean => {
  if (typeof value === 'number' && !isNaN(value)) {
    return true;
  }

  if (typeof value === 'string') {
    // 尝试将字符串转换为数字
    const num = Number(value);
    return !isNaN(num);
  }

  return false;
};

export function numberFormatToThousands(_num: string | number) {
  const num = Number(_num);
  if (Number.isNaN(num)) return 0;
  if (num < 0) return 0;
  if (num < 1000) {
    return num;
  }
  if (num === 1000) {
    return '1K';
  }
  if (num > 1000) {
    let newStr = '';
    let count = 0;
    const numK = (num / 1000).toFixed(1);
    const strArr = String(numK).split('.');
    let integerStr = strArr[0];
    for (let i = integerStr.length - 1; i >= 0; i--) {
      if (count % 3 === 0 && count !== 0) {
        newStr = `${integerStr.charAt(i)},${newStr}`;
      } else {
        newStr = integerStr.charAt(i) + newStr;
      }
      count++;
    }

    integerStr = `${newStr}${strArr[1] === '0' ? '' : `.${strArr[1]}`}K`;
    return integerStr;
  }
}
